import valid from 'card-validator';
import { onlyDigits } from './masks.js';

const FALLBACK_NUMBER_LENGTHS = [13, 14, 15, 16, 17, 18, 19];
const FALLBACK_NUMBER_GAPS = [4, 8, 12];
const DEFAULT_NUMBER_LENGTH = 16;
const GROUP_SIZE = 4;
const FALLBACK_SECURITY_CODE_SIZE = 3;
const MIN_HOLDER_NAME_LENGTH = 3;
const MAX_HOLDER_NAME_LENGTH = 100;

/** Groups the number the way the detected brand prints it (4-4-4-4, Amex 4-6-5, ...). */
export function maskCardNumber(value) {
  const digits = onlyDigits(value);
  const { lengths, gaps } = brandNumberFormat(digits);
  return groupDigits(digits.slice(0, Math.max(...lengths)), gaps);
}

/** Placeholder in the shape the detected brand prints most often. */
export function cardNumberPlaceholder(cardNumber) {
  const { lengths, gaps } = brandNumberFormat(cardNumber);
  const length = lengths.includes(DEFAULT_NUMBER_LENGTH) ? DEFAULT_NUMBER_LENGTH : Math.min(...lengths);

  return groupDigits('0'.repeat(length), gaps);
}

/** Digits the security code field accepts for the (partially) typed brand. */
export function securityCodeSize(cardNumber) {
  const { card } = valid.number(onlyDigits(cardNumber));
  return card?.code?.size || FALLBACK_SECURITY_CODE_SIZE;
}

/** Validates the number: brand, length and the Luhn check digit. */
export function validateCardNumber(value) {
  const digits = onlyDigits(value);
  if (!digits) return 'Número do cartão é obrigatório';
  if (!valid.number(digits).isValid) return 'Número do cartão inválido';
  return null;
}

/** Validates the holder name as it is printed on the card. */
export function validateCardHolderName(value) {
  const name = String(value || '').trim();
  if (!name) return 'Nome no cartão é obrigatório';
  if (name.length < MIN_HOLDER_NAME_LENGTH) return `Nome no cartão deve ter no mínimo ${MIN_HOLDER_NAME_LENGTH} caracteres`;
  if (name.length > MAX_HOLDER_NAME_LENGTH) return `Nome no cartão deve ter no máximo ${MAX_HOLDER_NAME_LENGTH} caracteres`;
  if (!valid.cardholderName(name).isValid) return 'Nome no cartão inválido';
  return null;
}

/** Validates an MM/AA expiration and rejects dates already past. */
export function validateCardExpiration(value) {
  const expiry = String(value || '').trim();
  if (!expiry) return 'Data de validade é obrigatória';

  const [month, year] = expiry.split('/');
  if (!/^\d{2}$/.test(month || '') || !/^\d{2}$/.test(year || '')) return 'Data de validade inválida';
  if (!valid.expirationMonth(month).isValid) return 'Mês inválido';
  if (!valid.expirationDate({ month, year }).isValid) return 'Data de validade expirada';
  return null;
}

/** Validates the security code length for the brand of the typed number. */
export function validateCardSecurityCode(value, cardNumber) {
  const digits = onlyDigits(value);
  if (!digits) return 'Código de segurança é obrigatório';
  if (!valid.cvv(digits, securityCodeSize(cardNumber)).isValid) return 'Código de segurança inválido';
  return null;
}

/** Validates the brand the API requires, auto-detected or confirmed by the donor. */
export function validateCardBrand(value) {
  if (!String(value || '').trim()) return 'Selecione a bandeira do cartão';
  return null;
}

/** Error message for one card field, or null when it is valid. */
export function validateCardField(field, details) {
  switch (field) {
    case 'cardNumber': return validateCardNumber(details.cardNumber);
    case 'cardBrand': return validateCardBrand(details.cardBrand);
    case 'cardName': return validateCardHolderName(details.cardName);
    case 'expiry': return validateCardExpiration(details.expiry);
    case 'cvv': return validateCardSecurityCode(details.cvv, details.cardNumber);
    default: return null;
  }
}

/** Errors keyed by card field; an empty object means the card is valid. */
export function validateCard(details) {
  const fields = ['cardNumber', 'cardBrand', 'cardName', 'expiry', 'cvv'];
  const errors = {};
  for (const field of fields) {
    const error = validateCardField(field, details);
    if (error) errors[field] = error;
  }
  return errors;
}

function brandNumberFormat(cardNumber) {
  const { card } = valid.number(onlyDigits(cardNumber));

  return {
    lengths: card?.lengths?.length ? card.lengths : FALLBACK_NUMBER_LENGTHS,
    gaps: card?.gaps?.length ? card.gaps : FALLBACK_NUMBER_GAPS
  };
}

function groupDigits(digits, gaps) {
  let masked = '';
  let start = 0;

  for (const gap of groupBoundaries(digits.length, gaps)) {
    if (digits.length <= gap) break;
    masked += `${digits.slice(start, gap)} `;
    start = gap;
  }

  return masked + digits.slice(start);
}

// Brands that print in even groups of four (Visa, Mastercard, Elo, ...) declare
// gaps only up to 16 digits, so longer numbers keep the same cadence. Brands with
// a distinct layout (Amex 4-6-5, Diners 4-6-4) follow their declared gaps.
function groupBoundaries(length, gaps) {
  const groupsOfFour = gaps.every((gap, index) => gap === (index + 1) * GROUP_SIZE);
  if (!groupsOfFour) return gaps;

  return Array.from({ length: Math.ceil(length / GROUP_SIZE) }, (_, index) => (index + 1) * GROUP_SIZE);
}
